import { useState, useEffect, useCallback } from 'react';

// Vault Management API - credentials from .env
const VAULT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
const VAULT_API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

// Category type from Vault API
export interface Category {
  id: string;
  name: string;
}

// Company types
export interface Company {
  id: string;
  name: string;
  slug: string;
  category_id: string | null;
  employees: string | null;
  url: string | null;
  logo_url: string | null;
  description: string | null;
  created_at?: string;
  updated_at?: string;
  pages?: CompanyPage[];
}

export interface CompanyPage {
  id: string;
  subslug: string;
  pricing_page_url: string | null;
  product_description: string | null;
  history_summary: string | null;
  archived: boolean | null;
}

export interface CompanyUpdate {
  name?: string;
  category_id?: string;
  employees?: string;
  url?: string;
  logo_url?: string;
  description?: string;
}

export interface PageUpdate {
  product_description?: string;
  history_summary?: string;
}

interface UseManagementAPIReturn {
  company: Company | null;
  pages: CompanyPage[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  updateCompany: (updates: CompanyUpdate) => Promise<Company | null>;
  updatePage: (pageId: string, updates: PageUpdate) => Promise<CompanyPage | null>;
  archiveAllPages: () => Promise<{ success: number; failed: number; errors: string[] }>;
  unarchiveAllPages: () => Promise<{ success: number; failed: number; errors: string[] }>;
  refetch: () => Promise<void>;
}

export function useManagementAPI(slug: string): UseManagementAPIReturn {
  const [company, setCompany] = useState<Company | null>(null);
  const [pages, setPages] = useState<CompanyPage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories from Vault API
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${VAULT_API_BASE}/categories`, {
        method: 'GET',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[useManagementAPI] Failed to fetch categories');
        return;
      }

      const responseData = await response.json();
      const categoriesData = responseData.data || responseData;
      setCategories(categoriesData);
      console.log('[useManagementAPI] Fetched categories:', categoriesData.length);
    } catch (err) {
      console.error('[useManagementAPI] Error fetching categories:', err);
    }
  }, []);

  // Fetch company by slug from Vault API
  const fetchCompany = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${VAULT_API_BASE}/companies/slug/${slug}`, {
        method: 'GET',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch company: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('[useManagementAPI] Fetched company:', responseData);
      // API returns { data: {...} } structure
      const companyData = responseData.data || responseData;
      setCompany(companyData);
      return companyData;
    } catch (err) {
      console.error('[useManagementAPI] Error fetching company:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch company');
      setCompany(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Fetch pages for company from Vault API
  const fetchPages = useCallback(async (companyId: string) => {
    if (!companyId) return;

    try {
      const response = await fetch(`${VAULT_API_BASE}/pages?company_id=${companyId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[useManagementAPI] Failed to fetch pages');
        return;
      }

      const responseData = await response.json();
      // API returns { pages: [...], total, limit, offset }
      const pagesData = responseData.pages || [];
      console.log('[useManagementAPI] Fetched pages:', pagesData.length);
      setPages(pagesData);
    } catch (err) {
      console.error('[useManagementAPI] Error fetching pages:', err);
      setPages([]);
    }
  }, []);

  // Update company via Vault API
  const updateCompany = useCallback(async (updates: CompanyUpdate): Promise<Company | null> => {
    if (!company?.id) {
      console.error('[useManagementAPI] Cannot update: no company id');
      return null;
    }

    try {
      console.log('[useManagementAPI] Updating company:', updates);

      const response = await fetch(`${VAULT_API_BASE}/companies/${company.id}`, {
        method: 'PATCH',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update company: ${response.status}`);
      }

      const responseData = await response.json();
      const updatedCompany = responseData.data || responseData;
      console.log('[useManagementAPI] Company updated:', updatedCompany);
      setCompany(updatedCompany);
      return updatedCompany;
    } catch (err) {
      console.error('[useManagementAPI] Error updating company:', err);
      throw err;
    }
  }, [company?.id]);

  // Update page via Vault API
  const updatePage = useCallback(async (pageId: string, updates: PageUpdate): Promise<CompanyPage | null> => {
    if (!pageId) {
      console.error('[useManagementAPI] Cannot update page: no page id');
      return null;
    }

    try {
      console.log('[useManagementAPI] Updating page:', pageId, updates);

      const response = await fetch(`${VAULT_API_BASE}/pages/${pageId}`, {
        method: 'PATCH',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update page: ${response.status}`);
      }

      const responseData = await response.json();
      const updatedPage = responseData.data || responseData;
      console.log('[useManagementAPI] Page updated:', updatedPage);

      // Update local pages state
      setPages(prev => prev.map(p => p.id === pageId ? { ...p, ...updatedPage } : p));

      return updatedPage;
    } catch (err) {
      console.error('[useManagementAPI] Error updating page:', err);
      throw err;
    }
  }, []);

  // Archive all pages for the company
  const archiveAllPages = useCallback(async () => {
    if (pages.length === 0) {
      return { success: 0, failed: 0, errors: [] };
    }

    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];

    console.log(`[useManagementAPI] Archiving ${pages.length} pages...`);

    for (const page of pages) {
      try {
        const response = await fetch(`${VAULT_API_BASE}/pages/${page.id}`, {
          method: 'PATCH',
          headers: {
            'X-API-Key': VAULT_API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ archived: true })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to archive page: ${response.status}`);
        }

        successCount++;
        console.log(`[useManagementAPI] Archived page: ${page.id}`);
      } catch (err) {
        failedCount++;
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        errors.push(`Page ${page.subslug || page.id}: ${errorMsg}`);
        console.error(`[useManagementAPI] Error archiving page ${page.id}:`, err);
      }
    }

    // Refetch pages to update local state
    if (company?.id) {
      await fetchPages(company.id);
    }

    console.log(`[useManagementAPI] Archive complete: ${successCount} success, ${failedCount} failed`);
    return { success: successCount, failed: failedCount, errors };
  }, [pages, company?.id, fetchPages]);

  // Unarchive all pages for the company
  const unarchiveAllPages = useCallback(async () => {
    if (pages.length === 0) {
      return { success: 0, failed: 0, errors: [] };
    }

    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];

    console.log(`[useManagementAPI] Unarchiving ${pages.length} pages...`);

    for (const page of pages) {
      try {
        const response = await fetch(`${VAULT_API_BASE}/pages/${page.id}`, {
          method: 'PATCH',
          headers: {
            'X-API-Key': VAULT_API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ archived: false })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to unarchive page: ${response.status}`);
        }

        successCount++;
        console.log(`[useManagementAPI] Unarchived page: ${page.id}`);
      } catch (err) {
        failedCount++;
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        errors.push(`Page ${page.subslug || page.id}: ${errorMsg}`);
        console.error(`[useManagementAPI] Error unarchiving page ${page.id}:`, err);
      }
    }

    // Refetch pages to update local state
    if (company?.id) {
      await fetchPages(company.id);
    }

    console.log(`[useManagementAPI] Unarchive complete: ${successCount} success, ${failedCount} failed`);
    return { success: successCount, failed: failedCount, errors };
  }, [pages, company?.id, fetchPages]);

  // Refetch function
  const refetch = useCallback(async () => {
    const companyData = await fetchCompany();
    if (companyData?.id) {
      await fetchPages(companyData.id);
    }
  }, [fetchCompany, fetchPages]);

  // Initial fetch on mount and when slug changes
  useEffect(() => {
    const init = async () => {
      fetchCategories();
      const companyData = await fetchCompany();
      if (companyData?.id) {
        await fetchPages(companyData.id);
      }
    };
    init();
  }, [fetchCategories, fetchCompany, fetchPages]);

  return {
    company,
    pages,
    categories,
    loading,
    error,
    updateCompany,
    updatePage,
    archiveAllPages,
    unarchiveAllPages,
    refetch
  };
}
